import { camelCase, range } from 'lodash-es';
import * as React from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import PurchaseDeletableTextInput from '../common/forms/PurchaseDeletableTextInput';
import PurchaseFormErrorMessage from '../common/forms/PurchaseFormErrorMessage';
import PurchaseSelect from '../common/forms/PurchaseSelect';
import { TextInputType } from '../common/forms/PurchaseTextInput';
import PurchaseLabeledContainer from '../common/PurchaseLabeledContainer';
import PurchaseSecurityCodeMessage from '../common/texts/PurchaseSecurityCodeMessage';
import * as $ from '../helper';

interface Props {
  creditCard: IPurchases.CreditCard;
  onChange: (creditCard: IPurchases.CreditCard) => void;
  error?: IPurchases.CreditCardError;
}

export const submittable = (creditCard: IPurchases.CreditCard): boolean =>
  !!creditCard.cardNumber &&
  !!creditCard.securityCode &&
  !!creditCard.month &&
  !!creditCard.year;

class PaymentCreditCard extends React.Component<Props & InjectedIntlProps> {
  constructor(props: Props & InjectedIntlProps) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
  }

  private yearOptions = this.createYearOptions();
  private monthOptions = this.createMonthOptions();

  private handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) {
    const {
      currentTarget: { name, value },
    } = e;
    const changedState = $.changeObjectByNestedString(
      { creditCard: { ...this.props.creditCard } },
      name,
      value,
    );
    this.props.onChange((changedState as Props).creditCard);
  }

  private createYearOptions() {
    const thisYear = new Date().getFullYear();
    return range(thisYear, thisYear + 15).map(year => ({
      label: year.toString(),
      value: year,
    }));
  }

  private createMonthOptions() {
    return range(1, 12 + 1).map(month => ({
      label: month.toString(),
      value: month,
    }));
  }

  public render() {
    const { creditCard, error, intl } = this.props;

    return (
      <div>
        {error && error.field === 'card' && (
          <div>
            {intl.formatMessage({
              id: `common.stripe.errorMessage.${camelCase(error.type)}`,
            })}
          </div>
        )}
        <PurchaseLabeledContainer
          label={intl.formatMessage({
            id: 'purchases.paymentCreditCard.creditCardNumber',
          })}
          creditCardLabel={true}
          paddingTop={true}
          requiredLabel={true}
          errorStyle={error && error.field === 'number'}
        >
          <PurchaseDeletableTextInput
            type={TextInputType.Text}
            value={creditCard.cardNumber || ''}
            name="creditCard.cardNumber"
            onChange={this.handleChange}
          />
        </PurchaseLabeledContainer>
        {error && error.field === 'number' && (
          <PurchaseFormErrorMessage>
            {intl.formatMessage({
              id: `common.stripe.errorMessage.${camelCase(error.type)}`,
            })}
          </PurchaseFormErrorMessage>
        )}
        <PurchaseLabeledContainer
          label={intl.formatMessage({
            id: 'purchases.paymentCreditCard.securityCode',
          })}
          requiredLabel={true}
          errorStyle={error && error.field === 'cvc'}
        >
          <PurchaseDeletableTextInput
            type={TextInputType.Text}
            value={creditCard.securityCode || ''}
            name="creditCard.securityCode"
            onChange={this.handleChange}
          />
        </PurchaseLabeledContainer>
        {error && error.field === 'cvc' && (
          <PurchaseFormErrorMessage>
            {intl.formatMessage({
              id: `common.stripe.errorMessage.${camelCase(error.type)}`,
            })}
          </PurchaseFormErrorMessage>
        )}
        <PurchaseSecurityCodeMessage />
        <PurchaseLabeledContainer
          label={intl.formatMessage({
            id: 'purchases.paymentCreditCard.expirationYear',
          })}
          requiredLabel={true}
          errorStyle={error && error.field === 'year'}
        >
          <PurchaseSelect
            name="creditCard.year"
            value={creditCard.year || ''}
            onChange={this.handleChange}
            selected={creditCard.year !== undefined}
          >
            {this.yearOptions.map((o, i) => (
              <FormattedMessage
                id="purchases.paymentCreditCard.year"
                values={{ year: o.label }}
                key={i}
              >
                {year => <option value={o.value}>{year}</option>}
              </FormattedMessage>
            ))}
          </PurchaseSelect>
        </PurchaseLabeledContainer>
        {error && error.field === 'year' && (
          <PurchaseFormErrorMessage>
            {intl.formatMessage({
              id: `common.stripe.errorMessage.${camelCase(error.type)}`,
            })}
          </PurchaseFormErrorMessage>
        )}
        <PurchaseLabeledContainer
          label={intl.formatMessage({
            id: 'purchases.paymentCreditCard.expirationMonth',
          })}
          requiredLabel={true}
          errorStyle={error && error.field === 'month'}
        >
          <PurchaseSelect
            name="creditCard.month"
            value={creditCard.month || ''}
            onChange={this.handleChange}
            selected={creditCard.month !== undefined}
          >
            {this.monthOptions.map((o, i) => (
              <FormattedMessage
                id="purchases.paymentCreditCard.month"
                values={{ month: o.label }}
                key={i}
              >
                {month => <option value={o.value}>{month}</option>}
              </FormattedMessage>
            ))}
          </PurchaseSelect>
        </PurchaseLabeledContainer>
        {error && error.field === 'month' && (
          <PurchaseFormErrorMessage>
            {intl.formatMessage({
              id: `common.stripe.errorMessage.${camelCase(error.type)}`,
            })}
          </PurchaseFormErrorMessage>
        )}
      </div>
    );
  }
}

export default injectIntl(PaymentCreditCard);
